import { FaultLogApi } from "@/services/FaultLog.api";
import { AlertAckConfigList, BatteryFaultCodes, Constants, DomainConstants, FaultCodeConstants } from "../Constants";
import { DataType, FaultLogLogisticsImpact, FaultLogPriority, FaultLogStatus, FilterOperator } from "../Enums";
import { CountryRegionDomain } from "../models/CountryRegionDomain";
import { FaultLog } from "../models/FaultLog";
import { DateTimeHelper } from "./DateTime.helper";
import { isEditor, isHandler } from "./roles";
import { Utils } from "./Utils";
import { IDashboardState } from "@/redux/dashboardSlice";
import { AdvancedSearchFilterItem } from "../models/advanced-search/AdvancedSearchFilterItem";

export class BusinessHelper {
  static IsNeedAck(faultLog: FaultLog, userDomain: string, userRoles: string[]): boolean {
    if (userDomain !== faultLog.domain) {
      return false;
    }

    // DFCT0055925 - need logic for when 2nd message received with "No readings" impact on logistics
    if (
      faultLog.notifyNumber > 1 &&
      BusinessHelper.IsFaultLogOpen(faultLog) &&
      faultLog.impactOnLogistics === FaultLogLogisticsImpact.NoReadings &&
      faultLog.faultCode?.toLowerCase().includes(Constants.FAULT_CODE_BATTERY_MARK)
    ) {
      if (this.IsNARegion(faultLog.region!) && (isHandler(userRoles) || isEditor(userRoles))) {
        return true;
      }

      if (!this.IsNARegion(faultLog.region!) && isHandler(userRoles)) {
        return true;
      }
    }

    // #region DFCT0055922 - fault not recovered logic needs to be split by fault type
    // Solar battery - panel disconnected
    // Mains battery - power disconnected
    // NA Logic:
    // Spontaneous recovery flag not received for 48 hours -> flash notification to Fault Editor
    if (
      BusinessHelper.IsFaultLogOpen(faultLog) &&
      (faultLog.faultCode === FaultCodeConstants.SolarBatteryPanelDisconnected ||
        faultLog.faultCode === FaultCodeConstants.MainsBatteryPowerDisconnected) &&
      this.IsNARegion(faultLog.region!) &&
      isEditor(userRoles) &&
      faultLog.isSpontaneouslyRecovered !== true &&
      DateTimeHelper.isOlderThanByHours(
        faultLog.createdAt!,
        parseInt(process.env.REACT_APP_EU_SPONTANEOUS_RECOVERY_FLASHING_ONE_WEEK_HOUR!)
      )
    ) {
      return true;
    }

    //EU Logic:
    // Spontaneous recovery flag not received for 7 days (High Priority Faults) or 14 days (Low Priority Faults)
    // -> flash another notification to Fault Handler
    if (
      BusinessHelper.IsFaultLogOpen(faultLog) &&
      faultLog.faultCode === FaultCodeConstants.MainsBatteryPowerDisconnected &&
      !this.IsNARegion(faultLog.region!) &&
      isHandler(userRoles) &&
      faultLog.isSpontaneouslyRecovered !== true &&
      ((DateTimeHelper.isOlderThanByHours(
        faultLog.createdAt!,
        parseInt(process.env.REACT_APP_EU_SPONTANEOUS_RECOVERY_FLASHING_ONE_WEEK_HOUR!)
      ) &&
        faultLog.priority === FaultLogPriority.High) ||
        (DateTimeHelper.isOlderThanByHours(
          faultLog.createdAt!,
          parseInt(process.env.REACT_APP_EU_SPONTANEOUS_RECOVERY_FLASHING_TWO_WEEK_HOUR!)
        ) &&
          faultLog.priority === FaultLogPriority.Low))
    ) {
      return true;
    }
    // #endregion

    // DFCT0056506
    // Battery faults with impact on logistics "No readings" aren't flashing to fault handler
    if (
      BatteryFaultCodes.includes(faultLog.faultCode!) &&
      faultLog.impactOnLogistics === FaultLogLogisticsImpact.NoReadings &&
      isHandler(userRoles)
    ) {
      return true;
    }

    // DFCT0056566
    // If 'Notification Outstanding Without Order' status description received, flash a notification to fault editor
    // If 'Field Work Is Done' status description is received, flash a notification to fault editor
    if (
      ((faultLog.notificationStatusDesc
        ?.toLocaleLowerCase()
        .includes(Constants.NotificationStatus_NotificationCompleted.toLocaleLowerCase()) &&
        Utils.isEmpty(faultLog.workOrderUserStatus) &&
        Utils.isEmpty(faultLog.workOrderSystemStatus)) ||
        faultLog.workOrderUserStatus
          ?.toLocaleLowerCase()
          .includes(Constants.WorkOrderUserStatus_FieldWorkIsDone.toLocaleLowerCase())) &&
      isEditor(userRoles)
    ) {
      return true;
    }

    return (
      (isHandler(userRoles) && faultLog.isSendLogistics) ||
      BusinessHelper.IsDisPlayAlertAndAckRequired(faultLog.faultCode!, faultLog.domain, userRoles?.[0], faultLog.status!)
    );
  }

  static IsFaultLogFlashing(faultLog: FaultLog, userDomain: string, userRoles: string[]) {
    if (userDomain !== faultLog.domain) {
      return false;
    }

    // Manually-triggered logistics notification on a closed fault must flash for the handler regardless of createdBy
    if (
      faultLog.status === FaultLogStatus.Closed &&
      faultLog.isSendLogistics === true &&
      // acknowledged is nullable, so treat null/undefined as not acknowledged to stay consistent with the dashboard query
      faultLog.acknowledged !== true &&
      isHandler(userRoles)
    ) {
      return true;
    }

    if (faultLog.createdBy?.toLocaleLowerCase() === Constants.CREATE_BY_LEGACY) {
      return false;
    }

    if (
      (faultLog.acknowledged === false && faultLog.needAck === true) ||
      (faultLog.workOrderUserStatus?.toLocaleUpperCase().includes(Constants.WorkOrderUserStatus_Done) &&
        isEditor(userRoles) &&
        BusinessHelper.IsFaultLogOpen(faultLog))
    ) {
      return true;
    }

    if (BusinessHelper.IsEUHanlderSpontaneouslyRecoveredAndClosedItemFlashing(faultLog, userDomain, userRoles)) {
      return true;
    }

    if (BusinessHelper.IsSpontaneouslyRecoveredAndOpenForEditorFlashing(faultLog, userDomain, userRoles)) {
      return true;
    }

    return false;
  }

  // Flashing for spontaneously recovered and opened items for editors
  static IsSpontaneouslyRecoveredAndOpenForEditorFlashing(faultLog: FaultLog, userDomain: string, userRoles: string[]) {
    return (
      faultLog &&
      userDomain === faultLog.domain &&
      faultLog.isSpontaneouslyRecovered === true &&
      isEditor(userRoles) &&
      faultLog.status === FaultLogStatus.Open &&
      faultLog.sapNotificationNumber &&
      faultLog.sapNotificationNumber > 0
    );
  }

  static IsEUHanlderSpontaneouslyRecoveredAndClosedItemFlashing(faultLog: FaultLog, userDomain: string, userRoles: string[]) {
    const isFlashing =
      faultLog &&
      faultLog.isSpontaneouslyRecovered === true &&
      !this.IsNAFaultLog(faultLog) &&
      userDomain === faultLog.domain &&
      isHandler(userRoles) &&
      faultLog.status === FaultLogStatus.Closed &&
      faultLog.isSpontaneouslyRecoveredViewed !== true;

    if (
      isFlashing &&
      faultLog.faultCode?.toLowerCase() === FaultCodeConstants.PrimaryBatteryEndOfLife.toLowerCase() &&
      faultLog.impactOnLogistics?.toLowerCase() !== FaultLogLogisticsImpact.NoReadings.toLowerCase()
    ) {
      return false;
    }

    return isFlashing;
  }

  static async LoadEUHandlerSpontaneouslyRecoveredFlashingItems(
    queryParams: IDashboardState,
    countryRegionDomains: CountryRegionDomain[],
    userDomain: string,
    userRoles: string[]
  ) {
    if (isHandler(userRoles) && !BusinessHelper.IsNADomain(userDomain) && !queryParams.openAdvancedQuery) {
      var params = Utils.deepClone(queryParams);
      params.filterStatusList = [FaultLogStatus.Closed];
      params.advancedFilters = [
        {
          field: "isSpontaneouslyRecovered",
          operator: FilterOperator.Equal.toString(),
          value: "true",
          type: DataType.Boolean,
        } as AdvancedSearchFilterItem,
        {
          field: "isSpontaneouslyRecoveredViewed",
          operator: FilterOperator.Equal.toString(),
          value: "false",
          type: DataType.Boolean,
        } as AdvancedSearchFilterItem,
      ];

      return await FaultLogApi.query(params, countryRegionDomains, userDomain, userRoles, false, true);
    }

    return [];
  }

  // Manually closed + logistics-notified faults stay on the handler dashboard until acknowledged (no "viewed" flag for this case)
  static async LoadHandlerClosedNotifiedFlashingItems(
    queryParams: IDashboardState,
    countryRegionDomains: CountryRegionDomain[],
    userDomain: string,
    userRoles: string[]
  ) {
    if (!isHandler(userRoles) || queryParams.openAdvancedQuery) {
      return [];
    }

    var params = Utils.deepClone(queryParams);
    params.filterStatusList = [FaultLogStatus.Closed];
    params.advancedFilters = [
      {
        field: "isSendLogistics",
        operator: FilterOperator.Equal.toString(),
        value: "true",
        type: DataType.Boolean,
      } as AdvancedSearchFilterItem,
    ];

    const items = await FaultLogApi.query(params, countryRegionDomains, userDomain, userRoles, false, true);

    return items.filter(
      (item) =>
        // acknowledged is nullable, so filter client-side instead of in SQL where NULL would be dropped
        item.acknowledged !== true &&
        // already-viewed spontaneous recoveries match the same criteria; leave their existing "hide once viewed" behaviour intact
        !(item.isSpontaneouslyRecovered === true && item.isSpontaneouslyRecoveredViewed === true)
    );
  }

  static IsFaultLogOpen(faultlog: FaultLog): boolean {
    return (
      faultlog.status === FaultLogStatus.Open ||
      faultlog.status === FaultLogStatus.Draft ||
      faultlog.status === FaultLogStatus.NotToBeRepaired
    );
  }

  static IsNAFaultLog(faultlog: FaultLog): boolean {
    return !Utils.isEmpty(faultlog.region) && faultlog.region!.startsWith(Constants.NA_REGION_PREFIX);
  }

  static IsNARegion(region: string): boolean {
    return !Utils.isEmpty(region) && region.startsWith(Constants.NA_REGION_PREFIX);
  }

  static IsNADomain(domain: string): boolean {
    return !Utils.isEmpty(domain) && domain === DomainConstants.NA;
  }

  static GetFlashingColor(faultlog: FaultLog): string {
    if (
      faultlog.isSpontaneouslyRecovered === true ||
      faultlog.workOrderUserStatus?.toLocaleUpperCase() === Constants.WorkOrderUserStatus_Done ||
      // Manually-notified closed fault: green signals "resolved/notified", distinct from other closed-but-flashing cases (e.g. DFCT0056506) which must stay red
      (faultlog.status === FaultLogStatus.Closed && faultlog.isSendLogistics === true)
    ) {
      return Constants.FlashingColor_Green;
    }

    return Constants.FlashingColor_Red;
  }

  static GetDomainByRegion(region: string, countryRegionDomainList: CountryRegionDomain[]): string {
    const CountryRegionDomain = countryRegionDomainList.find((item) => item.region === region);
    return CountryRegionDomain ? CountryRegionDomain.domain : "";
  }

  static IsDisPlayAlertAndAckRequired(faultCode: string, domain: string, userRole: string, faultStatus: string) {
    return AlertAckConfigList.some(
      (config) =>
        config.faultCode === faultCode &&
        config.domains.includes(domain) &&
        config.userRoles.includes(userRole) &&
        config.status === faultStatus
    );
  }

  // Helper function to compare values and detect changes
  static DetectFaultLogFieldChanges(original: FaultLog, updated: FaultLog): string[] {
    const changes: string[] = [];

    // Helper to format array values
    const formatArray = (arr: any[] | undefined | null): string => {
      if (!arr || arr.length === 0) return "[]";
      return `[${arr.map((item) => `"${item}"`).join(", ")}]`;
    };

    // Compare each trackable field
    const fieldComparisons: Array<{
      field: keyof FaultLog;
      label: string;
      isArray?: boolean;
      isDate?: boolean;
    }> = [
      { field: "priority", label: "Priority" },
      { field: "region", label: "Region" },
      { field: "country", label: "Country" },
      { field: "terminal", label: "Terminal", isArray: true },
      { field: "faultCode", label: "Fault Code" },
      { field: "owner", label: "Owner" },
      { field: "status", label: "Status" },
      { field: "impactOnLogistics", label: "Impact On Logistics" },
      { field: "product", label: "Product", isArray: true },
      { field: "numOfDeliveries", label: "# of Deliveries in Last 30 Days", isArray: true },
      { field: "vesselId", label: "Vessel ID", isArray: true },
      { field: "telemetrySystem", label: "Telemetry System" },
      { field: "assetName", label: "Asset Name", isArray: true },
      { field: "deviceId", label: "Device ID", isArray: true },
      { field: "deviceType", label: "Device Type", isArray: true },
      { field: "customerFlag", label: "Customer Flags", isArray: true },
      { field: "customerName", label: "Customer Name", isArray: true },
      { field: "customerCity", label: "Customer City", isArray: true },
      { field: "customerStateCode", label: "Customer State Code", isArray: true },
      { field: "customerShipTo", label: "Customer Ship To", isArray: true },
      { field: "customerDemandType", label: "Customer Demand Type", isArray: true },
      { field: "customerPostCode", label: "Customer Post Code", isArray: true },
      { field: "sapNotificationNumber", label: "SAP Notification Number" },
      { field: "rootCause", label: "Root Cause" },
      { field: "workOrderNumber", label: "Work Order Number" },
      { field: "workOrderUserStatus", label: "Work Order User Status" },
      { field: "workOrderInstructions", label: "Work Order Instructions" },
      { field: "closedDate", label: "Closed Date", isDate: true },
    ];

    fieldComparisons.forEach(({ field, label, isArray, isDate }) => {
      let oldValue = original[field as keyof FaultLog];
      let newValue = updated[field as keyof FaultLog];

      if (isArray) {
        // Compare arrays
        const oldArr = Array.isArray(oldValue) ? oldValue : [];
        const newArr = Array.isArray(newValue) ? newValue : [];

        if (JSON.stringify(oldArr) !== JSON.stringify(newArr)) {
          changes.push(`${label}: ${formatArray(oldArr)} to ${formatArray(newArr)}`);
        }
      } else if (isDate) {
        // Compare dates
        const oldDate = oldValue ? DateTimeHelper.ToLocalString(oldValue as string) : "";
        const newDate = newValue ? DateTimeHelper.ToLocalString(newValue as string) : "";

        if (oldDate !== newDate) {
          // Format for display
          const oldDisplay = oldDate ? DateTimeHelper.ToLocalString(oldDate as string) : "(empty)";
          const newDisplay = newDate ? DateTimeHelper.ToLocalString(newDate as string) : "(empty)";
          changes.push(`${label}: ${oldDisplay} to ${newDisplay}`);
        }
      } else {
        // Compare regular values (string, number)
        const oldVal = oldValue?.toString() || "";
        const newVal = newValue?.toString() || "";

        if (oldVal !== newVal) {
          changes.push(`${label}: ${oldVal || "(empty)"} to ${newVal || "(empty)"}`);
        }
      }
    });

    return changes;
  }

  static IsCustomerManagedDevice(faultLog: FaultLog | undefined): boolean {
    if (!faultLog || !faultLog.deviceType || faultLog.deviceType.length === 0) {
      return false;
    }

    return (
      this.IsNAFaultLog(faultLog) &&
      faultLog.deviceType.every((deviceType) => deviceType.toLowerCase() === Constants.DEVICE_TYPE_PUBLISHED_DEVICE)
    );
  }
}
